import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

const ui = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  primarySoft: "#e6f6f9",
  text: "#1f2933",
  muted: "#6b7280",
  border: "#e5e7eb",
  surface: "#f7f9fb",
  success: "#2e7d32",
  successSoft: "#e8f5e9",
  danger: "#d32f2f",
  dangerSoft: "#ffebee",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ui.primary },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: ui.primary,
    },
    "&.Mui-disabled": { backgroundColor: "#f3f5f7" },
  },
  "& label.Mui-focused": { color: ui.primary },
};

const primaryButtonSx = {
  backgroundColor: ui.primary,
  color: "#fff",
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  px: 3,
  py: 1.25,
  boxShadow: "none",
  "&:hover": { backgroundColor: ui.primaryDark, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#d5dbe0", color: "#8a949e" },
};

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const VacationActionDialog = ({
  openModal,
  actionType,
  actionLoading,
  selectedRequest,
  comment,
  setComment,
  handleCloseModal,
  handleSubmitAction,
}) => {
  return (
    <Dialog
      open={openModal}
      onClose={actionLoading ? undefined : handleCloseModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", m: { xs: 2, sm: 4 } } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          fontWeight: 700,
          color: ui.text,
          pb: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor:
              actionType === "Accept" ? ui.successSoft : ui.dangerSoft,
            color: actionType === "Accept" ? ui.success : ui.danger,
          }}
        >
          {actionType === "Accept" ? (
            <CheckCircleOutlineIcon fontSize="small" />
          ) : (
            <CancelOutlinedIcon fontSize="small" />
          )}
        </Box>
        {actionType === "Accept"
          ? "Accept vacation request"
          : "Reject vacation request"}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5 }}>
        <Typography sx={{ mb: 2, color: ui.text }}>
          Are you sure you want to {actionType.toLowerCase()} this vacation
          request for{" "}
          <strong>{selectedRequest?.userName || "Unknown User"}</strong>?
        </Typography>
        {selectedRequest && (
          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: "12px",
              backgroundColor: ui.surface,
              border: `1px solid ${ui.border}`,
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 1.5,
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ color: ui.muted }}>
                Start date
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {formatDate(selectedRequest.startDate)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: ui.muted }}>
                End date
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {formatDate(selectedRequest.endDate)}
              </Typography>
            </Box>
          </Box>
        )}
        <TextField
          label="Comment (Required)"
          multiline
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          fullWidth
          variant="outlined"
          sx={fieldSx}
          required
          disabled={actionLoading}
          error={!comment.trim()}
          helperText={!comment.trim() ? "Comment is required" : ""}
        />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleCloseModal}
          disabled={actionLoading}
          sx={{ color: ui.muted, textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmitAction}
          disabled={!comment.trim() || actionLoading}
          startIcon={
            actionLoading ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : null
          }
          sx={{
            ...primaryButtonSx,
            py: 1,
            backgroundColor: actionType === "Accept" ? ui.primary : ui.danger,
            "&:hover": {
              backgroundColor:
                actionType === "Accept" ? ui.primaryDark : "#b71c1c",
            },
            "&.Mui-disabled": { backgroundColor: "#d5dbe0", color: "#8a949e" },
          }}
        >
          {actionType === "Accept" ? "Accept request" : "Reject request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VacationActionDialog;